import { useAlert } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getApiV1ClassroomdisciplineGradeAndFrequenceCourseRegistrationsCourseregistrationidGradeSimulationPdf } from '@/shared/api/generated';
import { downloadFile } from '@/shared/ui/utils/downloadFile';

/**
 * Download imperativo da declaração de notas e frequência (PDF) de uma matrícula.
 *
 * Chama `GET /api/v1/classroomdiscipline/grade-and-frequence/course-registrations/
 * {courseRegistrationId}/grade-simulation/pdf` com `responseType: 'blob'` (o client gerado
 * não define esse header, sem ele o Axios corrompe o binário) e aciona o download nativo.
 * Em falha, dispara um alert — espelha o tratamento de erro da feature (`useGradeConfig`).
 *
 * @returns `{ download, downloadingId, isDownloading }`.
 *
 * @example
 * ```tsx
 * const { download, isDownloading } = useDownloadDeclaration();
 * <Button isLoading={isDownloading} onClick={() => download(id, 'declaracao.pdf')} />
 * ```
 */
export function useDownloadDeclaration() {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { alert } = useAlert();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const download = async (courseRegistrationId: string, fileName: string): Promise<void> => {
    setDownloadingId(courseRegistrationId);
    try {
      const data =
        await getApiV1ClassroomdisciplineGradeAndFrequenceCourseRegistrationsCourseregistrationidGradeSimulationPdf(
          Number(courseRegistrationId),
          { responseType: 'blob' },
        );
      const blob = data instanceof Blob ? data : new Blob([data as BlobPart]);
      downloadFile({ fileName, data: blob });
    } catch {
      alert({ alertType: 'warning', title: t('declaration.downloadError') });
    } finally {
      setDownloadingId(null);
    }
  };

  return { download, downloadingId, isDownloading: downloadingId !== null } as const;
}
